import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import InstallApp from "./pages/InstallApp";

// Auth pages
import { LoginPage } from "./pages/auth/Login";
import ForgotPasswordPage from "./pages/auth/ForgetPassword";
import ForgotPasswordSuccessPage from "./pages/auth/ForgotPasswordSuccess";
import InvalidTokenPage from "./pages/auth/InvalidTokenPage";
import TokenVerificationPage from "./pages/auth/TokenVerificationPage";
import SubscriptionExpired from "./pages/Subscribe";

// Contexts
import { ModalProvider } from "./context/ConfirmModalContext";
import { NetworkProvider } from "./context/NetworkContext";
import { ToastProvider } from "./context/ToastContext";

// Auth protection
import { ProtectedRoute } from "./components/ProtectedRoute";

// Core pages
import TestPage from "./pages/Test";
import DashboardPage from "./pages/dashboard/Dashboard";
import User from "./pages/staff/StaffList";
import CreateUser from "./pages/staff/CreateUpdateStaff";
import SettingsPage from "./pages/settings/SettingList";
import NotFoundPage from "./pages/NotFound";
import AssignStaffIDPage from "./pages/staff/StaffId";
import Roster from "./pages/staff/Roster";
import StaffProfile from "./pages/staff/Profile";
import CelebrationPage from "./pages/staff/Celebration";
import ComplaintPage from "./pages/MaintenanceLog";
import PaymentProcessing from "./pages/VerifyTransaction";
import LogMenu from "./pages/logs/LogMenu";
import ContactLogs from "./pages/logs/contact/ContactList";
import ContactDetails from "./pages/logs/contact/ContactDetail";
import ApplicantLogs from "./pages/logs/job-applicant/JobApplicantList";
import ApplicantDetails from "./pages/logs/job-applicant/JobApplicantDetails";
import AttendancePage from "./pages/staff/attendance/AttendancePage";
import SiteListPage from "./pages/settings/Site";
import CreateSitePage from "./pages/settings/CreateSite";
import AttendanceLog from "./pages/logs/attendance/AttendanceList";
import AttendanceDetails from "./pages/logs/attendance/AttendanceDetail";
import AttendanceStatus from "./pages/logs/attendance/AttendanceStatus";
import AccessDenied from "./pages/AccessDenied";
import { ProjectReports } from "./pages/project-reports/ProjectReport";
import AttendanceHistoryPage from "./pages/staff/attendance/StaffAttendanceList";
import NoNetwork from "./pages/NoNetwork";
import Partners from "./pages/Partner";
import PayrollSettings from "./pages/settings/Payroll";
import ExpensesPage from "./pages/expenses/ExpenseList";
import ExpenseDetailsPage from "./pages/expenses/ExpenseDetails";
import CreateExpenseRequestPage from "./pages/expenses/CreateExpenseRequest";
import DocumentCenterPage from "./pages/DocumentCenter";
import ExpenseSummaryPage from "./pages/expenses/ExpenseSummary";
import ProjectReportDetails from "./pages/project-reports/ProjectDetail";


export default function App() {
  return (
    <NetworkProvider interval={3000}>
      <ModalProvider>
        <ToastProvider>
          <Routes>

            <Route path="/payment/verify" element={<PaymentProcessing />} />

            {/* ---------- Public Routes ---------- */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/forgot-password/success" element={<ForgotPasswordSuccessPage />} />
            <Route path="/:action/verify" element={<TokenVerificationPage />} />
            <Route path="/:action/invalid" element={<InvalidTokenPage />} />
            <Route path="/download" element={<InstallApp />} />
            <Route path="/celebration" element={< CelebrationPage />} />

            {/* Redirect root to login */}
            <Route path="/" element={<Navigate to="/login" replace />} />

            <Route path="/subscription" element={<SubscriptionExpired />} />
            <Route path="/test" element={
              <ProtectedRoute>
                <TestPage />
              </ProtectedRoute>
            } />

            <Route path="/no-network" element={<NoNetwork />} />

            <Route path="/access-denied" element={<AccessDenied />} />

            {/* ---------- Protected Routes ---------- */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DashboardPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <LogMenu />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/partners"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Partners />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/project-reports"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ProjectReports />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/project-reports/:id"
              element={
                <ProtectedRoute>
                  <ProjectReportDetails />
                </ProtectedRoute >
              }
            />


            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <DocumentCenterPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/contacts"
              element={
                <ProtectedRoute>
                  <ContactLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/contacts/:id"
              element={
                <ProtectedRoute>
                  <ContactDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/applicants"
              element={
                <ProtectedRoute>
                  <ApplicantLogs />
                </ProtectedRoute>
              }
            />

            <Route
              path="/attendances"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <AttendancePage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/attendance-logs"
              element={
                <ProtectedRoute>
                  <AttendanceLog />
                </ProtectedRoute>
              }
            />

            <Route
              path="/attendance/history"
              element={
                <ProtectedRoute>
                  <AttendanceHistoryPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/attendances/:id"
              element={
                <ProtectedRoute>
                  <AttendanceDetails />
                </ProtectedRoute>
              }
            />


            <Route
              path="/attendance/status/:status"
              element={
                <ProtectedRoute>
                  < AttendanceStatus />
                </ProtectedRoute>
              }
            />

            <Route
              path="/applicants/:id"
              element={
                <ProtectedRoute>
                  <ApplicantDetails />
                </ProtectedRoute>
              }
            />

            <Route
              path="/staffs"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <User />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/staffs/register"
              element={
                <ProtectedRoute>
                  {/* <DashboardLayout> */}
                  <CreateUser />
                  {/* </DashboardLayout> */}
                </ProtectedRoute>
              }
            />

            <Route
              path="/staffs/update"
              element={
                <ProtectedRoute>
                  <CreateUser />
                </ProtectedRoute>
              }
            />

            <Route
              path="/staffs/signup"
              element={
                <ProtectedRoute>
                  <CreateUser />
                </ProtectedRoute>
              }
            />

            <Route
              path="/staffs/assign-id"
              element={
                <ProtectedRoute>
                  <AssignStaffIDPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <StaffProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staffs/update"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <CreateUser />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/roster"
              element={
                <ProtectedRoute>
                  <Roster />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <SettingsPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/sites"
              element={
                <ProtectedRoute>
                  <SiteListPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/payroll"
              element={
                <ProtectedRoute>
                  <PayrollSettings />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/sites/create"
              element={
                <ProtectedRoute>
                  <CreateSitePage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/settings/sites/create/:id/edit"
              element={
                <ProtectedRoute>
                  <CreateSitePage />
                </ProtectedRoute>
              }
            />


            <Route
              path="/maintenances"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ComplaintPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <ExpensesPage />
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />


            <Route
              path="/expenses/create"
              element={
                <ProtectedRoute>
                  {/* <DashboardLayout> */}
                  <CreateExpenseRequestPage />
                  {/* </DashboardLayout> */}
                </ProtectedRoute>
              }
            />

            <Route
              path="/expenses/summary"
              element={
                <ProtectedRoute>
                  {/* <DashboardLayout> */}
                  <ExpenseSummaryPage />
                  {/* </DashboardLayout> */}
                </ProtectedRoute>
              }
            />



            <Route
              path="/expenses/:expenseId"
              element={
                <ProtectedRoute>
                  <ExpenseDetailsPage />
                </ProtectedRoute>
              }
            />
            {/* ---------- 404 fallback ---------- */}
            <Route path="/404" element={<NotFoundPage />} />
            <Route path="*" element={<NotFoundPage />} />

          </Routes>
        </ToastProvider>
      </ModalProvider>
    </NetworkProvider>
  );
}
